/*
 *   File : users.js
 *   Author : https://evoqins.com
 *   Description : users component
 *   Integrations : sub-admin/customer-list
 *   Version : 1.0.1
 */
import React, { useState, useEffect } from "react";
import { Tabs } from "../../../Components/Navigation";
import { Filter } from "../../../Components/Filters";
import { DataTableComponent } from "../../../Components/DataTable";
import { Chip, EmptyScreen, Loader } from "../../../Components/Other";
import { PrimaryButton } from "../../../Components/Buttons";
import { useNavigate } from "react-router-dom";
import { APIService } from "../../../Service";
import { toast } from "react-toastify";

const TAB_DATA = [
  { id: 0, label: "invested", name: "Invested" },
  { id: 1, label: "non-invested", name: "Non Invested" },
];

export default function Users({ adminId }) {
  const navigate = useNavigate();
  const [appliedFilters, setAppliedFilters] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [filterData, setFilterData] = useState([]);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(12);
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [paginationData, setPaginationData] = useState(null);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const COLUMNS = [
    {
      name: "User ID",
      selector: (row) => row.customer_id,
      width: "120px",
      cell: (row) => `USR${String(row.customer_id).padStart(3, "0")}`,
    },

    {
      name: "User Name",
      selector: (row) => row.name,
      wrap: true,
      width: "200px",
      cell: (row) => (
        <div className="d-flex column-gap-2 align-items-center">
          <img
            src={require("../../../Assets/Images/avatar.jpg")}
            alt={row.name || "User"}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <span>{row.name || "N/A"}</span>
        </div>
      ),
    },

    {
      name: "Joined On",
      selector: (row) => row.joined_on,
      width: "160px",
    },

    // ⭐ RISK STATUS WITH SWITCH CASE
    {
      name: "Risk Status",
      selector: (row) => row.risk_appetite,
      width: "150px",
      cell: (row) => {
        const status = row.risk_appetite?.toLowerCase();

        switch (status) {
          case "high":
          case "very high":
            return (
              <Chip
                text={row.risk_appetite}
                style={"px-2 py-1 bg-copper-red bg-opacity-43"}
                textClr={"text-copper-red"}
              />
            );
          case "medium":
            return (
              <Chip
                text={"Medium"}
                style={"px-2 py-1 bg-facebook-blue bg-opacity-43"}
                textClr={"text-facebook-blue"}
              />
            );
          case "low":
          case "very low":
            return (
              <Chip
                text={row.risk_appetite}
                style={"px-2 py-1 bg-medium-sea-green bg-opacity-43"}
                textClr={"text-medium-sea-green"}
              />
            );
          case "n/a":
            return (
              <Chip
                text={"N/A"}
                style={"px-2 py-1 bg-platinum-gray bg-opacity-43"}
                textClr={"text-davys-gray"}
              />
            );
          default:
            return <span>{row.risk_appetite}</span>;
        }
      },
    },

    {
      name: "AUM",
      selector: (row) => row.aum,
      cell: (row) => (row.aum ? `₹${row.aum.toLocaleString("en-IN")}` : "₹0"),
    },

    {
      name: "Email",
      selector: (row) => row.email,
      width: "200px",
      cell: (row) => row.email || "N/A",
    },

    {
      name: "Phone Number",
      selector: (row) => row.phone_number,
      width: "180px",
    },

    // ⭐ KYC STATUS
    {
      name: "KYC Status",
      selector: (row) => row.kyc_status,
      width: "160px",
      cell: (row) => (
        <p
          className={`${
            row.kyc_status === "VERIFIED"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.kyc_status === "PENDING"
            ? "Pending"
            : row.kyc_status === "VERIFIED"
              ? "Verified"
              : row.kyc_status}
        </p>
      ),
    },

    // ⭐ USER STATUS
    {
      name: "User Status",
      selector: (row) => row.user_status,
      width: "160px",
      cell: (row) => (
        <p
          className={`${
            row.user_status === "ACTIVE"
              ? "text-medium-sea-green"
              : "text-copper-red"
          } mb-0`}
        >
          {row.user_status === "ACTIVE"
            ? "Active"
            : row.user_status === "INACTIVE"
              ? "Inactive"
              : row.user_status}
        </p>
      ),
    },

    {
      name: "Action",
      cell: (row) => (
        <PrimaryButton
          ClassName="bg-transparent fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue "
          name="View details"
          onPress={() => {
            navigate(`/users/detail/${row.customer_id}`);
          }}
        />
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];
  useEffect(() => {
    if (adminId) {
      _fetchCustomerList();
    }
  }, [page, perPage, activeTab, adminId, appliedFilters]);

  // Select All covers every page of the current list, so only a different list clears it
  useEffect(() => {
    setIsAllSelected(false);
  }, [perPage, activeTab, adminId, appliedFilters]);

  useEffect(() => {
    _getFilterData();
  }, []);
  // -------------------------------
  // FILTER HANDLER
  // -------------------------------
  const handleApply = (selectedValues) => {
    console.log("Filter applied:", selectedValues);
    setAppliedFilters(selectedValues);
    setPage(1); // Reset to first page when filters change
  };

  // -------------------------------
  // FETCH CUSTOMER LIST
  // -------------------------------
  const _fetchCustomerList = () => {
    setLoader(true);
    setIsEmpty(false);

    const payload = {
      sub_admin_id: parseInt(adminId),
      investment_type: activeTab,
      page_num: page,
      page_size: perPage,
      ...appliedFilters,
    };

    APIService(true, "sub-admin/customer-list", payload)
      .then((res) => {
        if (res.status_code === 200 && res.data) {
          const data = res.data;

          setTableData(data.customers || []);
          setTotalRows(data.total_count || 0);
          setPaginationData({
            totalPage: data.total_page,
            currentPage: data.current_page,
            page: data.page,
            record: data.record,
          });

          // Set isEmpty if no customers found
          if (!data.customers || data.customers.length === 0) {
            setIsEmpty(true);
          } else {
            setIsEmpty(false);
          }
        } else {
          toast.error(res.message || "Failed to load customer list");
          setTableData([]);
          setTotalRows(0);
          setIsEmpty(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching customer list:", error);
        toast.error("Failed to load customer list");
        setTableData([]);
        setTotalRows(0);
        setIsEmpty(true);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  //service to get filterData (normalize so Filter always receives an array; support multiple API shapes)
  const _getFilterData = () => {
    APIService(false, "user/filter-data")
      .then((res) => {
        if (!res) return;
        const raw = res.data ?? res.filters ?? res.users ?? res;
        const list = Array.isArray(raw)
          ? raw
          : (raw?.filters ??
              raw?.data ??
              raw?.items ??
              raw?.filter_options ??
              raw?.users ??
              []) ||
            [];
        setFilterData(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        console.error("Failed to load user filter data:", err);
      });
  };

  return (
    <div className="e-glass-card rounded-16 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center pt-24 px-24 pb-3">
        <Tabs
          data={TAB_DATA}
          defaultActive={activeTab}
          onChange={(tabId) => {
            console.log("ACTIVE TAB:", tabId);
            setActiveTab(tabId);
            setPage(1); // reset pagination on new tab
          }}
        />
        <Filter filters={filterData} onApply={handleApply} />
      </div>

      {loader ? (
        <Loader />
      ) : isEmpty ? (
        <EmptyScreen message={"No users under this sub admin"} />
      ) : (
        <>
          {/* PAGINATED TABLE */}
          <DataTableComponent
            columns={COLUMNS}
            info={true}
            data={tableData}
            pagination={true}
            paginationServer={true}
            selectableRows={true}
            allSelected={isAllSelected}
            onSelectedRowsChange={(state) => setIsAllSelected(state.allSelected)}
            currentPage={page}
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(newPerPage) => {
              setPerPage(newPerPage);
              setPage(1);
            }}
            className="e-table-basic"
          />
        </>
      )}
    </div>
  );
}
